import { useEffect, useState } from "react";
import api from "@/lib/api";
import { fmtMoney, fmtDate, EmptyState } from "@/components/Shared";
import { Link } from "react-router-dom";
import { Area, AreaChart, ResponsiveContainer, Tooltip } from "recharts";
import { ArrowUp, ArrowDown, Minus, ArrowRight, EnvelopeSimple, CheckSquare, Receipt, UserPlus, Lightning, HandCoins } from "@phosphor-icons/react";
import { ClientSummary, GrowthChart, AgingBuckets, TopDebtors, MrrWidget, ChurnWidget, FunnelWidget, ForecastWidget } from "@/components/DashboardWidgets";
import MiniTrend from "@/components/MiniTrend";
import MonthPicker from "@/components/MonthPicker";
import AnimatedNumber from "@/components/AnimatedNumber";
import { useI18n } from "@/i18n";
import ToolbarToggles from "@/components/ToolbarToggles";
import OutagesWidget from "@/components/OutagesWidget";

const PERIOD_KEYS = [
  { v: "today", k: "dash.period.today" },
  { v: "7d", k: "dash.period.7d" },
  { v: "month", k: "dash.period.month" },
  { v: "year", k: "dash.period.year" },
];
// Pre-filtered sub-arrays so JSX doesn't re-allocate per render.
const PERIOD_SHORTCUT = PERIOD_KEYS.filter(p => p.v === "today" || p.v === "7d");
const PERIOD_YEAR = PERIOD_KEYS.filter(p => p.v === "year");

function DeltaPill({ value, suffix = "%", inverse = false, hideThreshold = 5, dark = false }) {
  // No prior data, infinite, or unrealistic value → render nothing
  if (value === null || value === undefined || !isFinite(value)) return null;
  if (suffix === "%" && (value >= 100 || value <= -100)) return null;
  // Show only material deltas — under threshold render nothing to reduce noise
  if (Math.abs(value) < hideThreshold) return null;
  const positive = value > 0;
  const negative = value < 0;
  const good = inverse ? negative : positive;
  const bad = inverse ? positive : negative;
  const bgCls = good
    ? "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-400 dark:border-emerald-900"
    : bad
      ? "bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-950/40 dark:text-rose-400 dark:border-rose-900"
      : "bg-slate-50 text-slate-600 border-slate-200 dark:bg-slate-900 dark:text-slate-400 dark:border-slate-800";
  const Icon = value === 0 ? Minus : positive ? ArrowUp : ArrowDown;
  return (
    <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold font-mono border ${bgCls}`}>
      <Icon size={10} weight="bold" />
      {positive ? "+" : ""}{value.toFixed(1)}{suffix}
    </span>
  );
}

function Sparkline({ data }) {
  if (!data || data.length === 0) return null;
  return (
    <div className="h-full w-full" data-testid="hero-sparkline">
      <ResponsiveContainer width="100%" height={112}>
        <AreaChart data={data} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id="spark" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#FBBF24" stopOpacity={0.32} />
              <stop offset="100%" stopColor="#FBBF24" stopOpacity={0} />
            </linearGradient>
          </defs>
          <Tooltip
            cursor={{ stroke: "rgba(255,255,255,0.3)", strokeDasharray: "2 2" }}
            contentStyle={{ background: "#0B1F3A", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 6, fontSize: 11, padding: "4px 8px" }}
            labelStyle={{ color: "rgba(255,255,255,0.55)", fontSize: 10 }}
            itemStyle={{ color: "#FBBF24", padding: 0 }}
            formatter={(v) => [fmtMoney(v), "Cobrado"]}
            labelFormatter={(d) => new Date(d).toLocaleDateString("es-MX", { day: "numeric", month: "short" })}
          />
          <Area type="monotone" dataKey="amount" stroke="#FBBF24" strokeWidth={1.75} fill="url(#spark)" />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

function Stat({ label, value, delta, hint, loading, testid }) {
  return (
    <div className="px-5 py-4 border-r border-slate-200 last:border-r-0" data-testid={testid}>
      <div className="flex items-center justify-between mb-1.5">
        <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">{label}</div>
        <DeltaPill value={delta} />
      </div>
      <div className="text-xl font-semibold tracking-tight text-slate-900 font-mono">
        {loading ? <span className="text-slate-300">—</span> : value}
      </div>
      {hint && <div className="text-xs text-slate-500 mt-1">{hint}</div>}
    </div>
  );
}

function SectionCard({ title, eyebrow, action, children, testid }) {
  return (
    <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-md transition-all hover:border-slate-300 dark:hover:border-slate-700" data-testid={testid}>
      <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 dark:border-slate-800">
        <div>
          {eyebrow && <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400 font-semibold">{eyebrow}</div>}
          <h3 className="text-sm font-semibold text-slate-900 dark:text-white mt-0.5">{title}</h3>
        </div>
        {action}
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

function HeroCard({ fin, periodData, loading, t }) {
  const spark = periodData?.sparkline ?? [];
  const totalSpark = spark.reduce((a, d) => a + (d.amount || 0), 0);
  const ar = fin?.accounts_receivable ?? 0;
  const net = (fin?.total_income ?? 0) - (fin?.total_expenses ?? 0);
  const limited = !!fin?.limited;
  const mask = (v) => limited ? "$•••" : v;

  return (
    <div
      className="overflow-hidden border border-slate-800 relative rounded-md fade-in"
      data-testid="hero-card"
      style={{ background: "#0F172A" }}
    >
      {/* Thin amber rule on the left — single, restrained accent */}
      <div className="absolute left-0 top-0 bottom-0 w-[2px] bg-amber-400" />

      <div className="relative grid grid-cols-1 lg:grid-cols-12 divide-y lg:divide-y-0 lg:divide-x divide-slate-800">
        {/* LEFT — lifetime income headline */}
        <div className="lg:col-span-5 p-5 sm:p-6 lg:p-8">
          <div className="text-[10px] uppercase tracking-[0.24em] text-slate-500 font-semibold">{t("dash.hero.totalIngresos")}</div>
          <div className="text-4xl sm:text-5xl xl:text-[3.5rem] font-semibold tracking-tight font-mono mt-3 sm:mt-4 text-white tabular-nums break-all">
            {loading
              ? <span className="text-slate-700">— —</span>
              : limited
                ? "$•••"
                : <AnimatedNumber value={fin?.total_income ?? 0} format={fmtMoney} duration={1100} />
            }
          </div>
          <p className="text-xs text-slate-500 mt-3 leading-relaxed max-w-md">
            {t("dash.hero.totalHint")}
          </p>
          <div className="mt-5 flex items-center gap-6 text-xs">
            <div>
              <span className="text-white font-semibold font-mono">{loading ? "—" : fin?.paid_invoices_count ?? 0}</span>
              <span className="text-slate-500 ml-1.5">{t("dash.hero.cobradas")}</span>
            </div>
            <div>
              <span className="text-white font-semibold font-mono">{loading ? "—" : fin?.pending_or_overdue_count ?? 0}</span>
              <span className="text-slate-500 ml-1.5">{t("dash.hero.abiertas")}</span>
            </div>
          </div>
        </div>

        {/* CENTER — period collections + sparkline */}
        <div className="lg:col-span-4 p-5 sm:p-6 lg:p-8">
          <div className="flex items-start justify-between mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.24em] text-slate-500 font-semibold">{t("dash.hero.collected")} · {periodData?.range?.label || "periodo"}</div>
              <div className="text-2xl font-semibold font-mono text-white mt-1.5 tabular-nums">
                {loading
                  ? "—"
                  : limited
                    ? "$•••"
                    : <AnimatedNumber value={totalSpark ?? 0} format={fmtMoney} duration={900} />
                }
              </div>
            </div>
            <DeltaPill value={periodData?.deltas?.income} dark />
          </div>
          <div className="h-24">
            <Sparkline data={spark} />
          </div>
        </div>

        {/* RIGHT — AR + Net side metrics */}
        <div className="lg:col-span-3 p-5 sm:p-6 lg:p-8 flex flex-col justify-center gap-6">
          <div>
            <div className="text-[10px] uppercase tracking-[0.24em] text-slate-500 font-semibold">{t("dash.hero.receivable")}</div>
            <div className="text-xl font-semibold font-mono text-white mt-1.5 tabular-nums">
              {loading
                ? "—"
                : limited
                  ? "$•••"
                  : <AnimatedNumber value={ar ?? 0} format={fmtMoney} duration={900} />
              }
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.24em] text-slate-500 font-semibold">{t("dash.hero.netBalance")}</div>
            <div className={`text-xl font-semibold font-mono mt-1.5 tabular-nums ${net >= 0 ? "text-white" : "text-rose-400"}`}>
              {loading
                ? "—"
                : limited
                  ? "$•••"
                  : <AnimatedNumber value={net ?? 0} format={fmtMoney} duration={900} />
              }
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function PeriodMetricsRow({ periodData, loading, t }) {
  const rate = periodData?.current?.collection_rate ?? 0;
  const periodIncome = periodData?.current?.income ?? 0;
  const trend = periodData?.trend_3m || {};
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      <MetricCard
        testid="metric-rate"
        stagger={1}
        label={t("dash.kpi.collectionRate")}
        value={`${rate}%`}
        animatedNumber={rate}
        animatedFormat={(v) => `${Math.round(v)}%`}
        delta={periodData?.deltas?.collection_rate}
        deltaSuffix="pp"
        loading={loading}
        trend={trend.collection_rate}
        extra={
          <div className="mt-3 h-1 w-full bg-slate-100 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full ${rate >= 70 ? "bg-emerald-500" : rate >= 40 ? "bg-amber-500" : "bg-rose-500"}`}
              style={{ width: `${Math.min(100, rate)}%` }}
            />
          </div>
        }
        footer={periodData ? `${periodData.current.paid_count}/${periodData.current.issued_count} ${t("dash.kpi.invoices")}` : "—"}
      />
      <MetricCard
        testid="metric-balance"
        stagger={2}
        label={t("dash.kpi.periodBalance")}
        value={fmtMoney(periodData?.current?.balance)}
        animatedNumber={periodData?.current?.balance ?? 0}
        animatedFormat={fmtMoney}
        valueTone={(periodData?.current?.balance ?? 0) >= 0 ? "positive" : "negative"}
        delta={periodData?.deltas?.balance}
        loading={loading}
        trend={trend.balance}
        footer={`${t("dash.kpi.vs")} ${fmtMoney(periodData?.previous?.balance)} ${t("dash.kpi.prev")}`}
      />
      <MetricCard
        testid="metric-ticket"
        stagger={3}
        label={t("dash.kpi.avgTicket")}
        value={fmtMoney(periodData?.current?.avg_ticket)}
        animatedNumber={periodData?.current?.avg_ticket ?? 0}
        animatedFormat={fmtMoney}
        delta={periodData?.deltas?.avg_ticket}
        loading={loading}
        trend={trend.avg_ticket}
        footer={t("dash.kpi.perInvoice")}
      />
      <MetricCard
        testid="metric-income"
        stagger={4}
        label={t("dash.kpi.periodRevenue")}
        value={fmtMoney(periodIncome)}
        animatedNumber={periodIncome}
        animatedFormat={fmtMoney}
        delta={periodData?.deltas?.income}
        loading={loading}
        trend={trend.income}
        footer={`${t("dash.kpi.vs")} ${fmtMoney(periodData?.previous?.income)} ${t("dash.kpi.prev")}`}
      />
    </div>
  );
}

function MetricCard({ label, value, delta, deltaSuffix, footer, extra, loading, valueTone, testid, trend, animatedNumber, animatedFormat, stagger = 0 }) {
  const valueCls = valueTone === "positive" ? "text-slate-900 dark:text-white"
    : valueTone === "negative" ? "text-rose-700 dark:text-rose-400"
    : "text-slate-900 dark:text-white";
  const delayCls = stagger > 0 ? `delay-${Math.min(stagger, 6)}` : "";
  return (
    <div className={`bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-md p-5 transition-all hover:border-slate-300 dark:hover:border-slate-700 hover:shadow-sm hover:-translate-y-[1px] fade-in ${delayCls}`} data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400 font-semibold">{label}</div>
        <DeltaPill value={delta} suffix={deltaSuffix || "%"} />
      </div>
      <div className="flex items-end justify-between gap-2 mt-2">
        <div className={`text-xl font-semibold font-mono tracking-tight tabular-nums ${valueCls}`}>
          {loading
            ? <span className="text-slate-200 dark:text-slate-700">—</span>
            : (typeof animatedNumber === "number" && animatedFormat
                ? <AnimatedNumber value={animatedNumber} format={animatedFormat} duration={900} />
                : value)
          }
        </div>
        {trend && trend.length > 0 && (
          <div className="mb-0.5" data-testid={`${testid}-trend`}>
            <MiniTrend values={trend} width={72} height={22} />
          </div>
        )}
      </div>
      {extra}
      {footer && <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-3">{footer}</div>}
    </div>
  );
}

function SectionHeader({ eyebrow, title, hint, action }) {
  return (
    <div className="mb-5 border-b border-slate-200 dark:border-slate-800 pb-4 flex items-end justify-between gap-4">
      <div>
        <div className="text-[10px] uppercase tracking-[0.24em] text-amber-600 dark:text-amber-400 font-semibold">{eyebrow}</div>
        <h2 className="font-editorial text-[26px] leading-tight text-slate-900 dark:text-white mt-1.5">{title}</h2>
        {hint && <p className="text-xs text-slate-500 dark:text-slate-400 mt-1.5 max-w-xl">{hint}</p>}
      </div>
      {action}
    </div>
  );
}

export default function Dashboard() {
  const { t, lang } = useI18n();
  const [period, setPeriod] = useState("month");
  const [periodData, setPeriodData] = useState(null);
  const [overdue, setOverdue] = useState([]);
  const [fin, setFin] = useState(null);
  const [emailCfg, setEmailCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [periodLoading, setPeriodLoading] = useState(false);
  const [historicalMonth, setHistoricalMonth] = useState(""); // YYYY-MM or ""

  // Initial fetch
  useEffect(() => {
    (async () => {
      try {
        const [o, f, e] = await Promise.all([
          api.get("/reports/overdue-alerts"),
          api.get("/dashboard/financial-summary"),
          api.get("/config/email-status"),
        ]);
        setOverdue(o.data);
        setFin(f.data);
        setEmailCfg(e.data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Period-scoped data — re-fetch when period or historicalMonth changes
  useEffect(() => {
    (async () => {
      setPeriodLoading(true);
      try {
        const params = { period };
        if (historicalMonth && period === "month") params.month = historicalMonth;
        const { data } = await api.get(`/dashboard/period-data`, { params });
        setPeriodData(data);
      } finally {
        setPeriodLoading(false);
      }
    })();
  }, [period, historicalMonth]);

  const now = new Date();
  const [activeTab, setActiveTab] = useState("overview");
  const TABS = [
    { key: "overview", label: lang === "en" ? "Overview" : "Vista general" },
    { key: "collections", label: lang === "en" ? "Collections" : "Cobranza" },
    { key: "operations", label: lang === "en" ? "Operations" : "Operación" },
  ];

  return (
    <div>
      {/* Top toolbar with period selector */}
      <div className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 px-3 sm:px-4 lg:px-8 py-3 flex items-center justify-between gap-2 sm:gap-4 flex-wrap">
        <div className="flex items-center gap-1.5 sm:gap-3 text-xs min-w-0">
          <span className="hidden sm:inline text-slate-500">{t("common.workspace")}</span>
          <span className="hidden sm:inline text-slate-300">/</span>
          <span className="text-slate-900 dark:text-white font-medium truncate">{t("nav.dashboard")}</span>
          <span className="text-slate-300">·</span>
          <span className="text-slate-500 dark:text-slate-400 truncate">{periodData?.range?.label || t("dash.period.month")}</span>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-0 border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 rounded overflow-hidden" data-testid="period-selector">
            {PERIOD_SHORTCUT.map((p) => (
              <button
                key={p.v}
                onClick={() => { setPeriod(p.v); setHistoricalMonth(""); }}
                data-testid={`period-${p.v}`}
                className={`px-3 py-1.5 text-xs font-medium border-r border-slate-200 dark:border-slate-700 transition-colors ${
                  period === p.v ? "bg-slate-900 text-white" : "text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800"
                }`}
              >{t(p.k)}</button>
            ))}
            <MonthPicker
              value={historicalMonth}
              onChange={(v) => { setPeriod("month"); setHistoricalMonth(v); }}
              active={period === "month"}
              label={t("dash.period.month")}
              testid="period-month"
            />
            {PERIOD_YEAR.map((p) => (
              <button
                key={p.v}
                onClick={() => { setPeriod(p.v); setHistoricalMonth(""); }}
                data-testid={`period-${p.v}`}
                className={`px-3 py-1.5 text-xs font-medium border-l border-slate-200 dark:border-slate-700 last:border-r-0 transition-colors ${
                  period === p.v ? "bg-slate-900 text-white" : "text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800"
                }`}
              >{t(p.k)}</button>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-3 text-xs text-slate-500">
          {emailCfg && !emailCfg.enabled && (
            <span data-testid="email-mock-pill" className="inline-flex items-center gap-1.5 rounded px-2 py-1 bg-amber-50 text-amber-800 border border-amber-200 font-medium">
              <EnvelopeSimple size={12} /> {t("dash.email.mock")}
            </span>
          )}
          <ToolbarToggles />
          <span className="font-mono text-[11px] hidden md:inline">{now.toLocaleString(lang === "en" ? "en-US" : "es-MX", { dateStyle: "medium", timeStyle: "short" })}</span>
        </div>
      </div>

      <div className="p-3 sm:p-4 md:p-6 max-w-[1600px] mx-auto space-y-6 md:space-y-8">
        {/* Section tabs */}
        <div className="border-b border-slate-200 dark:border-slate-800 -mt-2 mb-2" data-testid="dashboard-tabs">
          <nav className="flex gap-1 overflow-x-auto">
            {TABS.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                data-testid={`dash-tab-${tab.key}`}
                className={`relative px-4 py-2.5 text-xs font-semibold uppercase tracking-wider transition-colors whitespace-nowrap ${
                  activeTab === tab.key
                    ? "text-slate-900 dark:text-white"
                    : "text-slate-500 hover:text-slate-800 dark:hover:text-slate-200"
                }`}
              >
                {tab.label}
                {activeTab === tab.key && (
                  <span className="absolute bottom-[-1px] left-0 right-0 h-[2px] bg-amber-500" />
                )}
              </button>
            ))}
          </nav>
        </div>

        {/* SECTION 1 — Global position */}
        {activeTab === "overview" && (
        <section data-testid="section-overview">
          <SectionHeader
            eyebrow={t("dash.hero.eyebrow")}
            title={t("dash.hero.title")}
            hint={periodData?.range?.historical
              ? (lang === "en" ? `Time-travel mode — viewing ${periodData?.range?.label}` : `Modo histórico — viendo ${periodData?.range?.label}`)
              : t("dash.hero.subtitle")}
          />
          <div className="space-y-6">
            <HeroCard fin={fin} periodData={periodData} loading={loading || periodLoading} t={t} />
            <PeriodMetricsRow periodData={periodData} loading={loading || periodLoading} t={t} />

            {/* Phase 1 — Business Metrics */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <MrrWidget lang={lang} />
              <ForecastWidget lang={lang} />
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <ChurnWidget lang={lang} />
              <FunnelWidget lang={lang} />
            </div>
          </div>
        </section>
        )}

        {/* SECTION 2 — Collections */}
        {activeTab === "collections" && (
        <section data-testid="section-collections">
          <SectionHeader
            eyebrow={t("dash.coll.eyebrow")}
            title={t("dash.coll.title")}
            hint={t("dash.coll.subtitle")}
          />
          <div className="space-y-6">
            <AgingBuckets lang={lang} />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <SectionCard
                testid="overdue-table"
                eyebrow={t("dash.coll.eyebrow")}
                title={`${t("dash.coll.overduePayments")} (${overdue.length})`}
                action={<Link to="/facturas" className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1">{t("dash.coll.goInvoices")} <ArrowRight size={12} /></Link>}
              >
                {overdue.length === 0 ? (
                  <EmptyState
                    icon={CheckSquare}
                    title={t("dash.coll.empty.title")}
                    message={t("dash.coll.empty.msg")}
                    compact
                  />
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left border-b border-slate-100 dark:border-slate-800">
                        <th className="pb-2 text-[10px] uppercase tracking-wider font-semibold text-slate-500">{t("common.client")}</th>
                        <th className="pb-2 text-[10px] uppercase tracking-wider font-semibold text-slate-500">{lang === "en" ? "Due date" : "Vencimiento"}</th>
                        <th className="pb-2 text-[10px] uppercase tracking-wider font-semibold text-slate-500 text-right">{t("common.amount")}</th>
                        <th className="pb-2 text-[10px] uppercase tracking-wider font-semibold text-slate-500 text-right">{lang === "en" ? "Days" : "Días"}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {overdue.slice(0, 10).map((inv) => {
                        const days = Math.max(0, Math.floor((Date.now() - new Date(inv.due_date).getTime()) / 86400000));
                        return (
                          <tr key={inv.id} className="border-b border-slate-50 dark:border-slate-800 last:border-0">
                            <td className="py-2.5 text-slate-900 dark:text-slate-100 font-medium">{inv.client_name}</td>
                            <td className="py-2.5 text-slate-600 dark:text-slate-400">{fmtDate(inv.due_date)}</td>
                            <td className="py-2.5 text-right font-mono text-red-600 font-semibold">{fmtMoney(inv.amount)}</td>
                            <td className="py-2.5 text-right text-slate-500 font-mono">{days}d</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </SectionCard>
              <TopDebtors lang={lang} />
            </div>
          </div>
        </section>
        )}

        {/* SECTION 3 — Operations (consolidated portfolio + growth) */}
        {activeTab === "operations" && (
        <section data-testid="section-operations">
          <SectionHeader
            eyebrow={lang === "en" ? "Operations" : "Operación"}
            title={lang === "en" ? "Portfolio & growth" : "Portafolio y crecimiento"}
            hint={lang === "en" ? "Client distribution and growth trends" : "Distribución de clientes y tendencias de crecimiento"}
          />
          <div className="space-y-6">
            <OutagesWidget />
            <ClientSummary />
            <GrowthChart />
          </div>
        </section>
        )}
      </div>
    </div>
  );
}
